/**
 * ✅ Headcount Verification Modal (admin only)
 * يقارن ملف إكسل بأكواد الموظفين (Client Code) بقاعدة البيانات ويُظهر كل الفروق
 *
 * Flow:
 *   1. choose scope  → one working company, or the whole client
 *   2. pick the Excel → POST /employees/headcount/verify (multipart)
 *   3. results       → banner + category chips + highlighted table + .xlsx report
 *   4. actions       → fix the differences without leaving the modal:
 *        Activate · Restore · Apply file values · Move to company
 *          → POST /employees/headcount/apply
 *        Resign / Terminate (payroll-aware, same as the Changes page)
 *          → POST /employee-actions/bulk/validate → /commit → poll /batches/:id
 *        Edit · View · Add → the page's own modals (onEditEmployee / onViewEmployee / onAddEmployee)
 *      After every action the same file is verified again automatically.
 *
 * The server does all the comparing and every write; this component only
 * uploads, renders and asks.
 * See HEADCOUNT-VERIFICATION-REPORT.md (user-service root).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx-js-style";
import {
  X, Upload, ShieldCheck, Download, RotateCcw, Search, FileSpreadsheet,
  UserCheck, ArchiveRestore, Wand2, Building2, UserMinus, Pencil, Eye, UserPlus, Loader2,
} from "lucide-react";
import { apiFetch } from "../../../../context/AuthContext";
import ReusableDropdown from "../../../../components/ReusableDropdown";
import useScreenSize from "../../../../hooks/useScreenSize";
import { makeStyles, scrollbarCss } from "../../../(Admin)Attendance/components/shiftControl/shiftControlStyles";
import bulkTranslations, { translateBulkMessage } from "../../../(Admin)EmployeeActions/Constant/EmployeeActions.bulk.i18n";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const PAGE = 300; // rows rendered at a time (big clients can have thousands)

// Category → label, colour, sort order. Red = in the file but NOT active,
// amber = active but NOT in the file, blue = active with a data difference.
const CATS = {
  INACTIVE_IN_SYSTEM: { en: "Not active in system", ar: "غير نشط في النظام", color: "#dc2626", fill: "FEE2E2" },
  ARCHIVED:           { en: "Archived (resigned)", ar: "مؤرشف (مستقيل)", color: "#dc2626", fill: "FEE2E2" },
  NOT_FOUND:          { en: "Not found in system", ar: "غير موجود في النظام", color: "#dc2626", fill: "FEE2E2" },
  OTHER_COMPANY:      { en: "Belongs to another company", ar: "تابع لشركة أخرى", color: "#c2410c", fill: "FFEDD5" },
  MISSING_FROM_FILE:  { en: "Active but missing from file", ar: "نشط لكنه غير موجود بالملف", color: "#d97706", fill: "FEF3C7" },
  DATA_MISMATCH:      { en: "Data differs", ar: "بيانات مختلفة", color: "#2563eb", fill: "DBEAFE" },
  ON_LEAVE:           { en: "On leave", ar: "في إجازة", color: "#7c3aed", fill: "EDE9FE" },
  DUPLICATE_IN_FILE:  { en: "Duplicated in file", ar: "مكرر في الملف", color: "#64748b", fill: "E2E8F0" },
  INVALID_ROW:        { en: "Row without client code", ar: "صف بدون كود", color: "#64748b", fill: "E2E8F0" },
  OK:                 { en: "Active & matching", ar: "نشط ومطابق", color: "#16a34a", fill: "DCFCE7" },
};
const ORDER = Object.keys(CATS);

const T = {
  en: {
    title: "Verify headcount",
    subtitle: "Upload the client's Excel list of client codes — every employee in it should be active. Every difference with the system is highlighted.",
    compareAgainst: "Compare against",
    company: "A specific company",
    wholeClient: "The whole client",
    chooseCompany: "Choose a company…",
    file: "Excel file",
    pick: "Choose file",
    hint: 'Required column: "Client Code". Optional, compared when present: Arabic Name, English Name, National ID, Working Company, Position, Department, Location, Hiring Date, Phone number, Email.',
    shortSubtitle: "Compare the client's Excel list with the active employees.",
    changeFile: "tap to change",
    fileTypes: "Excel (.xlsx, .xls) or CSV",
    requiredCol: "Required column:",
    optionalCols: "Optional (compared when present):",
    optionalList: "Arabic Name, English Name, National ID, Working Company, Position, Department, Location, Hiring Date, Phone number, Email.",
    close: "Close",
    verify: "Verify",
    verifying: "Verifying…",
    cancel: "Cancel",
    allGood: (n) => `Headcount verified — all ${n} employees in the file are active and match the system.`,
    notGood: (n) => `Headcount does not match — ${n} difference(s) found.`,
    fileRows: "In file",
    activeInSystem: "Active in system",
    issues: "All differences",
    everything: "Everything",
    search: "Search code or name…",
    row: "Excel row", code: "Client code", name: "Name", companyCol: "Company", status: "System status", result: "Result", details: "Details",
    nothing: "Nothing to show for this filter.",
    showMore: "Show more",
    again: "Verify another file",
    download: "Download report (.xlsx)",
    system: "system", inFile: "file",
    scopeLabel: (s) => (s.workingCompany ? `Company: ${s.workingCompany}` : `Whole client: ${s.clientName}`),
    compared: "Fields compared",
    none: "client code & status only",
    actions: "Actions",
    act: {
      activate: "Activate", restore: "Restore", apply: "Apply file values", move: "Move to company",
      separate: "Resign / Terminate", edit: "Edit", view: "View", add: "Add employee",
    },
    selectedN: (n) => `${n} selected`,
    clear: "Clear",
    selectAll: "Select all eligible rows shown",
    confirm: "Confirm",
    working: "Working…",
    confirmTitle: {
      activate: (n) => `Set ${n} employee(s) to Active?`,
      restore: (n) => `Restore ${n} archived employee(s)?`,
      applyFileValues: (n) => `Overwrite system data with the file values for ${n} employee(s)?`,
      move: (n, c) => `Move ${n} employee(s) to "${c}"?`,
    },
    confirmBody: {
      activate: "Only the status changes to Active. Payroll and past separation actions are not touched — use the Changes page if a separation must be reversed formally.",
      restore: "They are moved back from the archive to the live employee list as Active (same as the Restore button in Archived).",
      applyFileValues: "Each changed field below is written exactly as in the file. Department differences can't be applied here — use Edit.",
      move: "Their Working Company is changed. Chat groups and visibility follow the new company.",
    },
    resultTitle: (ok, bad) => `${ok} done${bad ? `, ${bad} failed` : ""}`,
    reverifying: "Re-checking the file…",
    sep: {
      title: (n) => `Separate ${n} employee(s)`,
      hint: "Creates real Resignation / Termination actions through the Changes page engine: approvals, history and final-payroll proration apply. A future last working day is scheduled — the employee stays active until then.",
      type: "Action", resignation: "Resignation", terminate: "Termination",
      lwd: "Last working day", reason: "Reason", details: "Details", termType: "Termination type",
      check: "Check", submit: (n) => `Submit ${n}`, back: "Back", done: "Done",
      valid: "Ready", warning: "With warnings", error: "Will be skipped",
      running: "Creating actions…", finished: "Finished",
    },
    fileVal: "file value",
  },
  ar: {
    title: "التحقق من عدد الموظفين",
    subtitle: "ارفع ملف الإكسل الخاص بالعميل والذي يحتوي على أكواد الموظفين — من المفترض أن يكون كل من فيه نشطًا. سيتم تمييز كل فرق مع النظام.",
    compareAgainst: "المقارنة مع",
    company: "شركة محددة",
    wholeClient: "العميل بالكامل",
    chooseCompany: "اختر الشركة…",
    file: "ملف الإكسل",
    pick: "اختر الملف",
    hint: 'العمود المطلوب: "Client Code". أعمدة اختيارية تتم مقارنتها إن وُجدت: الاسم العربي، الاسم الإنجليزي، الرقم القومي، الشركة، الوظيفة، القسم، الموقع، تاريخ التعيين، رقم الهاتف، البريد الإلكتروني.',
    shortSubtitle: "مقارنة ملف العميل بالموظفين النشطين في النظام.",
    changeFile: "اضغط للتغيير",
    fileTypes: "ملف إكسل (.xlsx, .xls) أو CSV",
    requiredCol: "العمود المطلوب:",
    optionalCols: "أعمدة اختيارية (تتم مقارنتها إن وُجدت):",
    optionalList: "الاسم العربي، الاسم الإنجليزي، الرقم القومي، الشركة، الوظيفة، القسم، الموقع، تاريخ التعيين، رقم الهاتف، البريد الإلكتروني.",
    close: "إغلاق",
    verify: "تحقق",
    verifying: "جارٍ التحقق…",
    cancel: "إلغاء",
    allGood: (n) => `تم التحقق — جميع الموظفين في الملف (${n}) نشطون ومطابقون للنظام.`,
    notGood: (n) => `العدد غير مطابق — تم العثور على ${n} فرق.`,
    fileRows: "في الملف",
    activeInSystem: "نشط في النظام",
    issues: "كل الفروق",
    everything: "الكل",
    search: "ابحث بالكود أو الاسم…",
    row: "صف الإكسل", code: "الكود", name: "الاسم", companyCol: "الشركة", status: "الحالة في النظام", result: "النتيجة", details: "التفاصيل",
    nothing: "لا توجد نتائج لهذا الفلتر.",
    showMore: "عرض المزيد",
    again: "تحقق من ملف آخر",
    download: "تحميل التقرير (.xlsx)",
    system: "النظام", inFile: "الملف",
    scopeLabel: (s) => (s.workingCompany ? `الشركة: ${s.workingCompany}` : `العميل بالكامل: ${s.clientName}`),
    compared: "الحقول التي تمت مقارنتها",
    none: "الكود والحالة فقط",
    actions: "الإجراءات",
    act: {
      activate: "تفعيل", restore: "استعادة", apply: "تطبيق قيم الملف", move: "نقل للشركة",
      separate: "استقالة / إنهاء خدمة", edit: "تعديل", view: "عرض", add: "إضافة موظف",
    },
    selectedN: (n) => `تم اختيار ${n}`,
    clear: "إلغاء الاختيار",
    selectAll: "اختيار كل الصفوف القابلة للإجراء المعروضة",
    confirm: "تأكيد",
    working: "جارٍ التنفيذ…",
    confirmTitle: {
      activate: (n) => `تفعيل ${n} موظف؟`,
      restore: (n) => `استعادة ${n} موظف من الأرشيف؟`,
      applyFileValues: (n) => `استبدال بيانات النظام بقيم الملف لعدد ${n} موظف؟`,
      move: (n, c) => `نقل ${n} موظف إلى "${c}"؟`,
    },
    confirmBody: {
      activate: "تتغير الحالة فقط إلى نشط. لا يتم تعديل الرواتب أو إجراءات إنهاء الخدمة السابقة — استخدم صفحة التغييرات لإلغاء إنهاء الخدمة رسميًا.",
      restore: "يتم نقلهم من الأرشيف إلى قائمة الموظفين كنشطين (مثل زر الاستعادة في الأرشيف).",
      applyFileValues: "يتم كتابة كل حقل مختلف كما هو في الملف. لا يمكن تطبيق فروق القسم هنا — استخدم التعديل.",
      move: "تتغير الشركة التي يعملون بها. مجموعات المحادثة والصلاحيات تتبع الشركة الجديدة.",
    },
    resultTitle: (ok, bad) => `تم تنفيذ ${ok}${bad ? `، وفشل ${bad}` : ""}`,
    reverifying: "جارٍ إعادة فحص الملف…",
    sep: {
      title: (n) => `إنهاء خدمة ${n} موظف`,
      hint: "ينشئ إجراءات استقالة / إنهاء خدمة حقيقية من خلال صفحة التغييرات: تطبق الموافقات والسجل وحساب الراتب الأخير. آخر يوم عمل في المستقبل يتم جدولته — ويبقى الموظف نشطًا حتى ذلك اليوم.",
      type: "الإجراء", resignation: "استقالة", terminate: "إنهاء خدمة",
      lwd: "آخر يوم عمل", reason: "السبب", details: "التفاصيل", termType: "نوع إنهاء الخدمة",
      check: "فحص", submit: (n) => `تنفيذ ${n}`, back: "رجوع", done: "تم",
      valid: "جاهز", warning: "مع تنبيهات", error: "سيتم تخطيه",
      running: "جارٍ إنشاء الإجراءات…", finished: "انتهى",
    },
    fileVal: "قيمة الملف",
  },
};

// Human sentence for one issue object returned by the server.
const issueText = (i, ar) => {
  switch (i.code) {
    case "MISSING_CLIENT_CODE": return ar ? "لا يوجد كود في هذا الصف" : "No client code in this row";
    case "DUPLICATE_OF_ROW": return ar ? `مكرر للصف ${i.row}` : `Duplicate of row ${i.row}`;
    case "FILE_CLIENT_DIFFERS": return ar ? `الملف يذكر العميل "${i.file}"` : `File says client "${i.file}"`;
    case "CODE_FORMAT_DIFFERS": return ar ? `الكود في الملف "${i.file}" وفي النظام "${i.system}"` : `Code written as "${i.file}" in file, "${i.system}" in system`;
    case "CODE_AMBIGUOUS": return ar ? `الكود يطابق أكثر من موظف: ${i.candidates.join("، ")}` : `Code matches several employees: ${i.candidates.join(", ")}`;
    case "ARCHIVED": return ar
      ? `مؤرشف${i.resignedDate ? ` — تاريخ الاستقالة ${i.resignedDate}` : ""}${i.reason ? ` (${i.reason})` : ""}`
      : `Archived${i.resignedDate ? ` — resigned ${i.resignedDate}` : ""}${i.reason ? ` (${i.reason})` : ""}`;
    case "NOT_FOUND": return ar ? "لا يوجد موظف بهذا الكود لدى هذا العميل" : "No employee with this code under this client";
    case "OTHER_COMPANY": return ar ? `يعمل في "${i.system || "—"}" وليس "${i.selected}"` : `Works at "${i.system || "—"}", not "${i.selected}"`;
    case "STATUS_NOT_ACTIVE": return ar ? `الحالة في النظام: ${i.status}` : `Status in system: ${i.status}`;
    case "STATUS_EMPTY": return ar ? "الحالة فارغة في النظام" : "Status is empty in the system";
    case "ON_LEAVE": return ar ? "في إجازة حاليًا" : "Currently on leave";
    case "MISSING_FROM_FILE": return ar ? "نشط في النظام لكنه غير مذكور في الملف" : "Active in the system but not in the file";
    case "MISSING_FROM_FILE_NO_CODE": return ar ? "نشط في النظام وليس له كود — لا يمكن مطابقته" : "Active in the system with NO client code — can't be matched";
    default: return i.code;
  }
};

// What can be done to a result row. `bulk` actions can run on a selection.
const rowActions = (r, selectedCompany) => {
  const has = !!r.infinityCode;
  const applicable = (r.diffs || []).filter((d) => d.applyKey);
  switch (r.category) {
    case "INACTIVE_IN_SYSTEM": return has ? ["activate", "edit"] : [];
    case "ARCHIVED": return has ? ["restore"] : [];
    case "MISSING_FROM_FILE": return has ? ["separate", "edit"] : [];
    case "OTHER_COMPANY": return has ? [...(selectedCompany ? ["move"] : []), "edit"] : [];
    case "DATA_MISMATCH": return has ? [...(applicable.length ? ["apply"] : []), "edit"] : [];
    case "NOT_FOUND": return ["add"];
    case "ON_LEAVE":
    case "OK": return has ? ["view"] : [];
    default: return [];
  }
};
const BULK = ["activate", "restore", "apply", "move", "separate"];
const ACT_ICON = {
  activate: UserCheck, restore: ArchiveRestore, apply: Wand2, move: Building2,
  separate: UserMinus, edit: Pencil, view: Eye, add: UserPlus,
};
const newKey = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `hc-${Date.now()}-${Math.random().toString(36).slice(2)}`;
const todayIso = () => new Date().toISOString().slice(0, 10);

export default function HeadcountVerifyModal({
  isOpen,
  onClose,
  companyOptions = [],
  theme = "light",
  language = "en",
  hidden = false,            // parent hides us while its Edit/View/Add modal is open
  refreshSignal = 0,         // parent bumps this when that modal closes → re-verify
  onEditEmployee,            // (infinityCode) => void
  onViewEmployee,            // (infinityCode) => void
  onAddEmployee,             // () => void
}) {
  const ar = language === "ar";
  const t = T[ar ? "ar" : "en"];
  const bt = bulkTranslations[ar ? "ar" : "en"];
  const dark = theme === "dark";
  const fileRef = useRef(null);

  const [scope, setScope] = useState("company");
  const [company, setCompany] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("ISSUES");
  const [query, setQuery] = useState("");
  const [limit, setLimit] = useState(PAGE);

  // actions
  const [selected, setSelected] = useState(() => new Set()); // keys = result index
  const [confirm, setConfirm] = useState(null);   // { action, items, n, preview[] }
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null); // { ok, bad, failures[] }
  const [sep, setSep] = useState(null);           // separation dialog state

  const c = {
    bg: dark ? "#0f172a" : "#ffffff",
    panel: dark ? "#1e293b" : "#f8fafc",
    border: dark ? "rgba(255,255,255,0.12)" : "rgba(15,23,42,0.12)",
    text: dark ? "#e2e8f0" : "#0f172a",
    dim: dark ? "#94a3b8" : "#64748b",
    accent: "#2e7cba",
  };
  // Same visual language as the other system dialogs (Shift Control, Remote
  // Punch): header icon tile, 10px controls, footer bar. Phones: bottom sheet.
  const { isMobile } = useScreenSize();
  const st = useMemo(() => makeStyles(theme, ar, isMobile), [theme, ar, isMobile]);

  const resetResult = () => {
    setData(null); setError(""); setFilter("ISSUES"); setQuery(""); setLimit(PAGE);
    setSelected(new Set()); setLastResult(null); setConfirm(null); setSep(null);
  };
  const resetAll = () => {
    resetResult(); setFile(null);
    if (fileRef.current) fileRef.current.value = "";
  };
  const close = () => { resetAll(); onClose?.(); };

  const verify = async ({ keepView = false } = {}) => {
    if (!file) return;
    setLoading(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("scope", scope);
      if (scope === "company") fd.append("workingCompany", company);
      const res = await apiFetch(`${BASE_URL}/employees/headcount/verify`, { method: "POST", body: fd });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body?.success) {
        const msg = body?.messages?.[ar ? "ar" : "en"] || body?.message || body?.error;
        throw new Error(msg || (res.status === 403 ? (ar ? "غير مسموح" : "Access denied") : `HTTP ${res.status}`));
      }
      body.results = body.results.map((r, i) => ({ ...r, _key: i }));
      setData(body);
      setSelected(new Set());
      if (!keepView) setFilter(body.summary.allMatch ? "ALL" : "ISSUES");
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  // Parent's Edit/View/Add modal closed → the data may have changed.
  const lastSignal = useRef(refreshSignal);
  useEffect(() => {
    if (refreshSignal !== lastSignal.current) {
      lastSignal.current = refreshSignal;
      if (data && file) verify({ keepView: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal]);

  const counts = useMemo(() => {
    const m = {};
    (data?.results || []).forEach((r) => { m[r.category] = (m[r.category] || 0) + 1; });
    return m;
  }, [data]);

  const selectedCompany = data?.summary?.workingCompany || null;

  const visible = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    return data.results
      .filter((r) =>
        filter === "ALL" ? true
          : filter === "ISSUES" ? r.category !== "OK" && r.category !== "ON_LEAVE"
          : r.category === filter)
      .filter((r) => !q || [r.clientCode, r.fileName, r.systemName, r.workingCompany]
        .some((v) => String(v ?? "").toLowerCase().includes(q)))
      .sort((a, b) => ORDER.indexOf(a.category) - ORDER.indexOf(b.category)
        || (a.rowNumber ?? 1e9) - (b.rowNumber ?? 1e9));
  }, [data, filter, query]);

  const isSelectable = (r) => rowActions(r, selectedCompany).some((a) => BULK.includes(a));
  const selectedRows = useMemo(
    () => (data ? data.results.filter((r) => selected.has(r._key)) : []),
    [data, selected],
  );
  // How many selected rows each bulk action applies to.
  const bulkCounts = useMemo(() => {
    const m = {};
    selectedRows.forEach((r) => rowActions(r, selectedCompany).forEach((a) => {
      if (BULK.includes(a)) m[a] = (m[a] || 0) + 1;
    }));
    return m;
  }, [selectedRows, selectedCompany]);

  const toggle = (key) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });
  const shownSelectable = visible.slice(0, limit).filter(isSelectable);
  const allShownSelected = shownSelectable.length > 0 && shownSelectable.every((r) => selected.has(r._key));
  const toggleAllShown = () => setSelected((prev) => {
    const next = new Set(prev);
    if (allShownSelected) shownSelectable.forEach((r) => next.delete(r._key));
    else shownSelectable.forEach((r) => next.add(r._key));
    return next;
  });

  // ── Run an action on rows ────────────────────────────────────────────────
  const startAction = (action, rows) => {
    const eligible = rows.filter((r) => rowActions(r, selectedCompany).includes(action));
    if (!eligible.length) return;
    if (action === "edit") return onEditEmployee?.(eligible[0].infinityCode);
    if (action === "view") return onViewEmployee?.(eligible[0].infinityCode);
    if (action === "add") return onAddEmployee?.();
    if (action === "separate") {
      return setSep({
        rows: eligible, step: "form", actionType: "resignation", lastWorkingDay: todayIso(),
        resignationReason: "", reason: "", terminationType: "", validation: null, batch: null, err: "",
      });
    }
    let apiAction = action;
    let items;
    let preview = [];
    if (action === "apply") {
      apiAction = "applyFileValues";
      items = eligible.map((r) => ({
        infinityCode: r.infinityCode,
        changes: Object.fromEntries(r.diffs.filter((d) => d.applyKey).map((d) => [d.applyKey, d.file])),
      }));
      preview = eligible.slice(0, 8).map((r) =>
        `${r.clientCode}: ${r.diffs.filter((d) => d.applyKey).map((d) => `${d.label} "${d.system ?? "—"}" → "${d.file}"`).join(", ")}`);
    } else if (action === "move") {
      apiAction = "applyFileValues";
      items = eligible.map((r) => ({ infinityCode: r.infinityCode, changes: { workingCompany: selectedCompany } }));
    } else {
      items = eligible.map((r) => ({ infinityCode: r.infinityCode }));
    }
    setConfirm({ action, apiAction, items, n: items.length, preview });
  };

  const runConfirmed = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      const res = await apiFetch(`${BASE_URL}/employees/headcount/apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: confirm.apiAction, items: confirm.items }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body?.success) {
        throw new Error(body?.messages?.[ar ? "ar" : "en"] || body?.message || body?.error || `HTTP ${res.status}`);
      }
      setLastResult({
        ok: body.succeeded,
        bad: body.failed,
        failures: body.results.filter((r) => !r.ok).map((r) =>
          `${r.clientCode || "—"}: ${(ar && r.messageAr) || r.message || r.code}`),
      });
      setConfirm(null);
      await verify({ keepView: true });
    } catch (e) {
      setLastResult({ ok: 0, bad: confirm.n, failures: [e.message || String(e)] });
      setConfirm(null);
    } finally {
      setBusy(false);
    }
  };

  // ── Separation (Resign / Terminate) via the Bulk Actions engine ──────────
  const sepRows = () => sep.rows.map((r, i) => ({
    rowId: i + 1,
    rowNumber: i + 2,
    infinityCode: r.infinityCode,
    clientCode: r.clientCode || "",
    employeeName: r.systemName || "",
    actionType: sep.actionType,
    lastWorkingDay: sep.lastWorkingDay,
    reason: sep.reason,
    resignationReason: sep.actionType === "resignation" ? sep.resignationReason : "",
    terminationType: sep.actionType === "terminate" ? sep.terminationType : "",
  }));
  const bulkFetch = async (path, init = {}) => {
    const res = await apiFetch(`${BASE_URL}/employee-actions/bulk${path}`, {
      ...init,
      headers: { "x-view-type": "admin", ...(init.body ? { "Content-Type": "application/json" } : {}), ...(init.headers || {}) },
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok || body?.success === false) {
      throw new Error(body?.message || body?.messages?.[ar ? "ar" : "en"] || body?.error || `HTTP ${res.status}`);
    }
    return body.data;
  };
  const sepCheck = async () => {
    setSep((s) => ({ ...s, busy: true, err: "" }));
    try {
      const validation = await bulkFetch("/validate", { method: "POST", body: JSON.stringify({ rows: sepRows() }) });
      setSep((s) => ({ ...s, busy: false, step: "review", validation }));
    } catch (e) {
      setSep((s) => ({ ...s, busy: false, err: e.message }));
    }
  };
  const sepSubmit = async () => {
    setSep((s) => ({ ...s, busy: true, err: "", step: "running" }));
    try {
      const started = await bulkFetch("/commit", {
        method: "POST",
        body: JSON.stringify({ rows: sepRows(), fileName: `headcount-${data?.fileName || "verification"}`, clientBatchKey: newKey() }),
      });
      let batch = null;
      for (let i = 0; i < 400; i++) { // ≤ 10 min
        batch = await bulkFetch(`/batches/${started.batchId}`);
        setSep((s) => ({ ...s, batch }));
        if (batch.status !== "processing") break;
        await new Promise((r) => setTimeout(r, 1500));
      }
      setSep((s) => ({ ...s, busy: false, step: "done", batch }));
    } catch (e) {
      setSep((s) => ({ ...s, busy: false, step: "review", err: e.message }));
    }
  };
  const sepDone = async () => {
    setSep(null);
    await verify({ keepView: true });
  };

  const downloadReport = () => {
    const header = [t.row, t.result, t.code, t.name + ` (${t.inFile})`, t.name + ` (${t.system})`, t.companyCol, t.status, t.details];
    const sorted = [...data.results].sort((a, b) => ORDER.indexOf(a.category) - ORDER.indexOf(b.category) || (a.rowNumber ?? 1e9) - (b.rowNumber ?? 1e9));
    // NOTE: infinityCode is deliberately NOT exported to the file.
    const rows = sorted.map((r) => [
      r.rowNumber ?? "—",
      CATS[r.category][ar ? "ar" : "en"],
      r.clientCode ?? "",
      r.fileName ?? "",
      r.systemName ?? "",
      r.workingCompany ?? "",
      r.systemStatus ?? "",
      [
        ...(r.diffs || []).map((d) => `${d.label}: ${t.inFile} "${d.file}" / ${t.system} "${d.system ?? ""}"`),
        ...(r.issues || []).map((i) => issueText(i, ar)),
      ].join(" | "),
    ]);
    const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
    header.forEach((_, ci) => {
      const ref = XLSX.utils.encode_cell({ r: 0, c: ci });
      ws[ref].s = { font: { bold: true, color: { rgb: "FFFFFF" } }, fill: { fgColor: { rgb: "2E7CBA" } } };
    });
    sorted.forEach((r, ri) => {
      header.forEach((_, ci) => {
        const ref = XLSX.utils.encode_cell({ r: ri + 1, c: ci });
        if (ws[ref]) ws[ref].s = { fill: { fgColor: { rgb: CATS[r.category].fill } } };
      });
    });
    ws["!cols"] = [8, 26, 14, 24, 24, 20, 14, 70].map((w) => ({ wch: w }));
    if (ar) ws["!views"] = [{ RTL: true }];

    const s = data.summary;
    const summarySheet = XLSX.utils.aoa_to_sheet([
      [t.title],
      [t.scopeLabel(s)],
      [ar ? "الملف" : "File", data.fileName],
      [ar ? "وقت التحقق" : "Verified at", new Date(data.verifiedAt).toLocaleString()],
      [t.fileRows, s.fileRows],
      [t.activeInSystem, s.activeInSystem],
      ...ORDER.map((k) => [CATS[k][ar ? "ar" : "en"], counts[k] || 0]),
    ]);
    summarySheet["!cols"] = [{ wch: 32 }, { wch: 40 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, summarySheet, ar ? "ملخص" : "Summary");
    XLSX.utils.book_append_sheet(wb, ws, ar ? "النتائج" : "Results");
    const tag = (s.workingCompany || s.clientName || "client").replace(/[\\/:*?"<>|]+/g, "-");
    XLSX.writeFile(wb, `headcount-verification-${tag}-${data.verifiedAt.slice(0, 10)}.xlsx`);
  };

  if (!isOpen) return null;

  const canVerify = !!file && !loading && (scope === "client" || !!company);
  const s = data?.summary;
  const muted = st.muted;
  const panelBorder = `1px solid ${c.border}`;
  const miniBtn = (color) => ({
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 5, padding: isMobile ? "7px 10px" : "5px 9px",
    borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", border: `1px solid ${color}55`,
    background: `${color}14`, color, fontFamily: "inherit",
  });
  const actColor = { activate: "#16a34a", restore: "#16a34a", apply: "#2563eb", move: "#c2410c", separate: "#dc2626", edit: c.dim, view: c.dim, add: "#2e7cba" };
  const inputStyle = { ...st.input, width: "100%" };
  const stepTitle = (n, label) => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 700, fontSize: 14, marginBottom: 10 }}>
      <span style={{ width: 24, height: 24, borderRadius: "50%", background: c.accent, color: "#fff", fontSize: 12.5, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{n}</span>
      {label}
    </div>
  );
  const notice = (tone, children, extra = {}) => {
    const color = { error: "#dc2626", success: "#16a34a", warning: "#d97706", info: c.accent }[tone];
    return (
      <div style={{ padding: "12px 14px", borderRadius: 12, fontSize: 13.5, lineHeight: 1.6, color: tone === "info" ? c.text : color, background: `${color}${dark ? "26" : "12"}`, border: `1px solid ${color}40`, ...extra }}>
        {children}
      </div>
    );
  };
  // Nested dialogs (confirm / separation) sit over the main dialog.
  const overlay = { position: "absolute", inset: 0, background: "rgba(2,6,23,0.45)", display: "flex", alignItems: isMobile ? "flex-end" : "center", justifyContent: "center", padding: isMobile ? 0 : 16, borderRadius: "inherit", zIndex: 5 };
  const card = { ...st.modal, maxWidth: 560, maxHeight: "85%", padding: isMobile ? "18px 16px" : "22px 24px", ...(isMobile && { borderRadius: "18px 18px 0 0" }) };
  const footerBtn = (variant, disabled, extra = {}) => ({ ...st.btn(variant, disabled), ...(isMobile && { flex: "1 1 0", minWidth: 0, whiteSpace: "normal" }), ...extra });
  const nameOf = (r) => r.systemName ?? r.fileName ?? "—";
  const sepBlocked = !!sep && (sep.busy || (sep.validation ? sep.validation.totals.valid + sep.validation.totals.warning === 0 : true));

  const renderActions = (r) => rowActions(r, selectedCompany).map((a) => {
    const Icon = ACT_ICON[a];
    return (
      <button key={a} type="button" disabled={busy || loading} onClick={() => startAction(a, [r])} style={miniBtn(actColor[a])}
        title={a === "move" ? `${t.act.move}: ${selectedCompany}` : t.act[a]}>
        <Icon size={13} /> {t.act[a]}
      </button>
    );
  });
  const renderDetails = (r) => (
    <>
      {(r.diffs || []).map((d) => (
        <div key={d.field}>
          <b>{d.label}</b>: <span style={{ color: "#dc2626", textDecoration: "line-through" }}>{String(d.system ?? "—")}</span>
          {" → "}<span style={{ color: "#16a34a", fontWeight: 600 }}>{String(d.file)}</span>
          <span style={{ color: c.dim, fontSize: 11 }}> ({t.system} → {t.inFile})</span>
        </div>
      ))}
      {(r.issues || []).map((iss, k) => <div key={k} style={{ color: c.dim }}>{issueText(iss, ar)}</div>)}
    </>
  );
  const catBadge = (cat) => (
    <span style={{ whiteSpace: "nowrap", padding: "3px 10px", borderRadius: 999, fontSize: 12, fontWeight: 700, color: cat.color, background: `${cat.color}22` }}>
      {cat[ar ? "ar" : "en"]}
    </span>
  );

  // ── Step 1: setup ──────────────────────────────────────────────────────────
  const setupBody = (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {notice("info", t.subtitle)}

      <section>
        {stepTitle(1, t.compareAgainst)}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(200px, 100%), 1fr))", gap: 10 }}>
          {[["company", t.company, Building2], ["client", t.wholeClient, ShieldCheck]].map(([k, label, Icon]) => {
            const active = scope === k;
            return (
              <button
                key={k}
                type="button"
                aria-pressed={active}
                onClick={() => { setScope(k); resetResult(); }}
                style={{
                  display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: 12, cursor: "pointer",
                  textAlign: "start", fontFamily: "inherit", fontSize: 14, fontWeight: 600, color: c.text, minHeight: 48,
                  border: active ? `2px solid ${c.accent}` : panelBorder,
                  background: active ? (dark ? "rgba(46,124,186,0.18)" : "rgba(46,124,186,0.08)") : (dark ? "rgba(255,255,255,0.03)" : "#ffffff"),
                }}
              >
                <span style={{ width: 18, height: 18, borderRadius: "50%", flexShrink: 0, boxSizing: "border-box", border: active ? `5px solid ${c.accent}` : `2px solid ${dark ? "#475569" : "#cbd5e1"}`, background: dark ? "#0f172a" : "#fff" }} />
                <Icon size={16} color={active ? c.accent : muted} style={{ flexShrink: 0 }} />
                {label}
              </button>
            );
          })}
        </div>
        {scope === "company" && (
          <div style={{ marginTop: 10 }}>
            <ReusableDropdown
              items={companyOptions.map((name) => ({ key: name, label: name }))}
              value={company}
              onSelect={(item) => setCompany(item.key)}
              placeholder={t.chooseCompany}
              getItemKey={(it) => it.key}
              getItemLabel={(it) => it.label}
              maxItems={200}
              theme={theme}
              language={language}
              ariaLabel={t.chooseCompany}
            />
          </div>
        )}
      </section>

      <section>
        {stepTitle(2, t.file)}
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,.xls,.xlsm,.csv,.ods"
          style={{ display: "none" }}
          onChange={(e) => { setFile(e.target.files?.[0] || null); setError(""); }}
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          style={{
            width: "100%", display: "flex", alignItems: "center", gap: 14, padding: "16px", borderRadius: 14, cursor: "pointer",
            fontFamily: "inherit", textAlign: "start", color: c.text,
            border: `1.5px dashed ${file ? "#16a34a" : c.accent}88`,
            background: file ? (dark ? "rgba(22,163,74,0.10)" : "#f0fdf4") : (dark ? "rgba(46,124,186,0.08)" : "#f5f9fd"),
          }}
        >
          <span style={{ width: 44, height: 44, borderRadius: 12, flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#fff", background: file ? "#16a34a" : c.accent }}>
            {file ? <FileSpreadsheet size={22} /> : <Upload size={22} />}
          </span>
          <span style={{ minWidth: 0, flex: 1 }}>
            <span style={{ display: "block", fontWeight: 700, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {file ? file.name : t.pick}
            </span>
            <span style={{ display: "block", fontSize: 12.5, color: muted, marginTop: 2 }}>
              {file ? `${(file.size / 1024).toFixed(0)} KB · ${t.changeFile}` : t.fileTypes}
            </span>
          </span>
        </button>
        <div style={{ marginTop: 10, fontSize: 12.5, color: muted, lineHeight: 1.7 }}>
          <div><b style={{ color: c.text }}>{t.requiredCol}</b> Client Code</div>
          <div><b style={{ color: c.text }}>{t.optionalCols}</b> {t.optionalList}</div>
        </div>
      </section>

      {error && notice("error", error)}
    </div>
  );

  // ── Step 2: results ────────────────────────────────────────────────────────
  const tiles = s ? [
    { label: t.fileRows, value: s.fileRows, color: c.accent },
    { label: t.activeInSystem, value: s.activeInSystem, color: "#16a34a" },
    { label: t.issues, value: s.issues, color: s.issues ? "#dc2626" : "#16a34a" },
  ] : [];
  const resultsBody = s && (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {notice(s.allMatch ? "success" : "error", (
        <>
          <div style={{ fontWeight: 700 }}>{s.allMatch ? t.allGood(s.fileRows) : t.notGood(s.issues)}</div>
          <div style={{ fontSize: 12.5, color: muted, marginTop: 2 }}>
            {t.compared}: {s.comparedFields.length ? s.comparedFields.join(", ") : t.none}
            {loading && <> · <Loader2 size={12} className="nx-spin" style={{ verticalAlign: "middle" }} /> {t.reverifying}</>}
          </div>
        </>
      ))}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: isMobile ? 8 : 12 }}>
        {tiles.map((tile) => (
          <div key={tile.label} style={{ padding: isMobile ? "10px" : "12px 16px", borderRadius: 12, border: panelBorder, background: dark ? "rgba(255,255,255,0.03)" : "#ffffff", borderTop: `3px solid ${tile.color}`, minWidth: 0 }}>
            <div style={{ fontSize: isMobile ? 11.5 : 12.5, color: muted, fontWeight: 600, lineHeight: 1.3 }}>{tile.label}</div>
            <div style={{ fontSize: isMobile ? 20 : 24, fontWeight: 800, color: tile.color, marginTop: 4 }}>{tile.value}</div>
          </div>
        ))}
      </div>

      {lastResult && notice(lastResult.bad ? "warning" : "success", (
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
          <div>
            <b>{t.resultTitle(lastResult.ok, lastResult.bad)}</b>
            {lastResult.failures.slice(0, 6).map((f, i) => <div key={i} style={{ color: c.dim }}>{f}</div>)}
            {lastResult.failures.length > 6 && <div style={{ color: c.dim }}>… +{lastResult.failures.length - 6}</div>}
          </div>
          <button type="button" onClick={() => setLastResult(null)} style={{ ...st.iconBtn(), alignSelf: "flex-start" }} aria-label={t.close}><X size={16} /></button>
        </div>
      ))}

      {/* Category filter: one scrolling line on phones */}
      <div className="sc-noscroll" style={{ display: "flex", flexWrap: isMobile ? "nowrap" : "wrap", overflowX: isMobile ? "auto" : "visible", gap: 6, paddingBottom: isMobile ? 2 : 0 }}>
        {[["ISSUES", t.issues, s.issues, "#dc2626"], ...ORDER.filter((k) => counts[k]).map((k) => [k, CATS[k][ar ? "ar" : "en"], counts[k], CATS[k].color]), ["ALL", t.everything, data.results.length, c.dim]]
          .map(([k, label, n, color]) => {
            const on = filter === k;
            return (
              <button key={k} type="button" onClick={() => { setFilter(k); setLimit(PAGE); }}
                style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 999, fontSize: 13, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", color: on ? "#fff" : c.text, background: on ? color : "transparent", border: `1.5px solid ${on ? color : c.border}` }}>
                {!on && <span style={{ width: 8, height: 8, borderRadius: 4, background: color }} />}
                {label} <b>{n}</b>
              </button>
            );
          })}
      </div>

      <div style={{ position: "relative" }}>
        <Search size={16} color={muted} style={{ position: "absolute", top: "50%", transform: "translateY(-50%)", insetInlineStart: 13, pointerEvents: "none" }} />
        <input className="sc-input" value={query} onChange={(e) => { setQuery(e.target.value); setLimit(PAGE); }} placeholder={t.search}
          style={{ ...inputStyle, paddingInlineStart: 38 }} />
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "10px 12px", borderRadius: 12, background: dark ? "rgba(46,124,186,0.15)" : "rgba(46,124,186,0.08)", border: `1px solid ${c.accent}55` }}>
          <b style={{ fontSize: 13, width: isMobile ? "100%" : "auto" }}>{t.selectedN(selected.size)}</b>
          {BULK.filter((a) => bulkCounts[a]).map((a) => {
            const Icon = ACT_ICON[a];
            return (
              <button key={a} type="button" disabled={busy || loading} onClick={() => startAction(a, selectedRows)} style={miniBtn(actColor[a])}>
                <Icon size={13} /> {a === "move" ? `${t.act.move}: ${selectedCompany}` : t.act[a]} ({bulkCounts[a]})
              </button>
            );
          })}
          <button type="button" onClick={() => setSelected(new Set())} style={{ ...miniBtn(c.dim), marginInlineStart: "auto" }}>{t.clear}</button>
        </div>
      )}

      {isMobile ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {shownSelectable.length > 0 && (
            <label style={{ ...st.checkRow, height: "auto", fontSize: 13, color: muted }}>
              <input type="checkbox" className="sc-check" checked={allShownSelected} onChange={toggleAllShown} />
              {t.selectAll}
            </label>
          )}
          {visible.slice(0, limit).map((r) => {
            const cat = CATS[r.category];
            const acts = renderActions(r);
            return (
              <article key={r._key} style={{ padding: 14, borderRadius: 14, border: panelBorder, borderInlineStart: `4px solid ${cat.color}`, background: r.category === "OK" ? "transparent" : `${cat.color}${dark ? "1a" : "0d"}`, display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                  {isSelectable(r) && (
                    <input type="checkbox" className="sc-check" style={{ marginTop: 2 }} checked={selected.has(r._key)} onChange={() => toggle(r._key)} aria-label={nameOf(r)} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 15, overflowWrap: "anywhere" }}>{nameOf(r)}</div>
                    {r.fileName && r.systemName && r.fileName !== r.systemName && (
                      <div style={{ fontSize: 12, color: muted }}>{t.inFile}: {r.fileName}</div>
                    )}
                    <div style={{ fontSize: 12, color: muted, marginTop: 2 }}>
                      <span style={{ fontFamily: "ui-monospace, monospace" }} dir="ltr">{r.clientCode ?? "—"}</span>
                      {r.rowNumber != null && ` · ${t.row} ${r.rowNumber}`}
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                  {catBadge(cat)}
                  <span style={{ fontSize: 12, color: muted }}>{[r.workingCompany, r.systemStatus].filter(Boolean).join(" · ")}</span>
                </div>
                {((r.diffs || []).length > 0 || (r.issues || []).length > 0) && (
                  <div style={{ fontSize: 12.5, lineHeight: 1.6, padding: "8px 10px", borderRadius: 10, background: dark ? "rgba(255,255,255,0.04)" : "#ffffff", border: panelBorder }}>
                    {renderDetails(r)}
                  </div>
                )}
                {acts.length > 0 && <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>{acts}</div>}
              </article>
            );
          })}
          {!visible.length && <div style={{ padding: 28, textAlign: "center", color: muted }}>{t.nothing}</div>}
        </div>
      ) : (
        <div className="sc-grid" style={{ overflow: "auto", border: panelBorder, borderRadius: 12, maxHeight: "52vh", minHeight: 220 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ position: "sticky", top: 0, background: c.panel, padding: "10px", borderBottom: panelBorder, zIndex: 1, width: 36 }}>
                  <input type="checkbox" className="sc-check" title={t.selectAll} checked={allShownSelected} disabled={!shownSelectable.length} onChange={toggleAllShown} />
                </th>
                {[t.row, t.code, t.name, t.companyCol, t.status, t.result, t.details, t.actions].map((h) => (
                  <th key={h} style={{ position: "sticky", top: 0, background: c.panel, textAlign: "start", padding: "10px", borderBottom: panelBorder, whiteSpace: "nowrap", zIndex: 1, fontSize: 12, fontWeight: 700, color: muted }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.slice(0, limit).map((r) => {
                const cat = CATS[r.category];
                const tint = `${cat.color}${dark ? "1f" : "0f"}`;
                return (
                  <tr key={r._key} style={{ background: r.category === "OK" ? "transparent" : tint, borderInlineStart: `4px solid ${cat.color}`, borderBottom: panelBorder }}>
                    <td style={{ padding: "9px 10px" }}>
                      {isSelectable(r) && <input type="checkbox" className="sc-check" checked={selected.has(r._key)} onChange={() => toggle(r._key)} />}
                    </td>
                    <td style={{ padding: "9px 10px", color: c.dim }}>{r.rowNumber ?? "—"}</td>
                    <td style={{ padding: "9px 10px", fontFamily: "ui-monospace, monospace", whiteSpace: "nowrap" }} dir="ltr">{r.clientCode ?? "—"}</td>
                    <td style={{ padding: "9px 10px", fontWeight: 600 }}>
                      {nameOf(r)}
                      {r.fileName && r.systemName && r.fileName !== r.systemName && (
                        <div style={{ fontSize: 12, color: c.dim, fontWeight: 400 }}>{t.inFile}: {r.fileName}</div>
                      )}
                    </td>
                    <td style={{ padding: "9px 10px" }}>{r.workingCompany ?? "—"}</td>
                    <td style={{ padding: "9px 10px" }}>{r.systemStatus ?? "—"}</td>
                    <td style={{ padding: "9px 10px" }}>{catBadge(cat)}</td>
                    <td style={{ padding: "9px 10px", lineHeight: 1.6 }}>{renderDetails(r)}</td>
                    <td style={{ padding: "9px 10px" }}>
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>{renderActions(r)}</div>
                    </td>
                  </tr>
                );
              })}
              {!visible.length && (
                <tr><td colSpan={9} style={{ padding: 28, textAlign: "center", color: c.dim }}>{t.nothing}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {visible.length > limit && (
        <div style={{ textAlign: "center" }}>
          <button type="button" className="sc-ghost" onClick={() => setLimit((l) => l + PAGE)} style={st.btn("ghost")}>{t.showMore} ({visible.length - limit})</button>
        </div>
      )}
    </div>
  );

  return (
    <div
      onClick={() => { if (!busy && !sep?.busy) close(); }}
      style={{ ...st.overlay, zIndex: 2000, display: hidden ? "none" : "flex", ...(isMobile && { padding: 0, alignItems: "flex-end" }) }}
    >
      <style>{scrollbarCss(dark)}</style>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t.title}
        onClick={(e) => e.stopPropagation()}
        style={{
          ...st.dialog,
          position: "relative",
          height: "auto",
          width: data ? "min(1280px, 100%)" : "min(640px, 100%)",
          ...(isMobile && { borderRadius: "18px 18px 0 0", maxHeight: "94dvh" }),
        }}
      >
        {/* Header — same layout as the other system dialogs */}
        <div style={{ ...st.header, flexWrap: "nowrap", alignItems: "flex-start", gap: 12, ...(isMobile && { padding: "14px 16px" }) }}>
          <div style={{ display: "flex", gap: isMobile ? 12 : 14, alignItems: "center", minWidth: 0, flex: "1 1 auto" }}>
            <div style={st.headerIcon}><ShieldCheck size={22} /></div>
            <div style={{ minWidth: 0 }}>
              <h2 style={{ ...st.title, ...(isMobile && { fontSize: 17 }) }}>{t.title}</h2>
              <p style={{ ...st.subtitle, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: isMobile ? "normal" : "nowrap" }}>
                {s ? `${t.scopeLabel(s)} · ${data.fileName}` : t.shortSubtitle}
              </p>
            </div>
          </div>
          <button type="button" className="sc-close" style={st.closeBtn} onClick={close} aria-label={t.close}><X size={22} /></button>
        </div>

        <div className="sc-noscroll" style={{ ...st.body, display: "block", ...(isMobile && { padding: "14px 16px" }) }}>
          {data ? resultsBody : setupBody}
        </div>

        <div style={{ ...st.footer, justifyContent: "flex-end", flexWrap: "nowrap", ...(isMobile && { padding: "12px 16px", gap: 10 }) }}>
          {data ? (
            <>
              <button type="button" className="sc-ghost" onClick={resetAll} style={footerBtn("ghost", false)}><RotateCcw size={16} /> {t.again}</button>
              <button type="button" className="sc-primary" onClick={downloadReport} style={footerBtn("primary", false)}><Download size={16} /> {t.download}</button>
            </>
          ) : (
            <>
              <button type="button" className="sc-ghost" onClick={close} style={footerBtn("ghost", false)}>{t.cancel}</button>
              <button type="button" className="sc-primary" onClick={() => verify()} disabled={!canVerify} style={footerBtn("primary", !canVerify)}>
                {loading ? <Loader2 size={16} className="nx-spin" /> : <ShieldCheck size={16} />} {loading ? t.verifying : t.verify}
              </button>
            </>
          )}
        </div>

        {/* Confirm dialog (activate / restore / apply / move) */}
        {confirm && (
          <div style={overlay} onClick={() => !busy && setConfirm(null)}>
            <div className="sc-noscroll" style={card} onClick={(e) => e.stopPropagation()}>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>
                {confirm.action === "move"
                  ? t.confirmTitle.move(confirm.n, selectedCompany)
                  : t.confirmTitle[confirm.apiAction](confirm.n)}
              </div>
              <div style={{ ...st.hint, fontSize: 13, marginBottom: 10 }}>
                {t.confirmBody[confirm.action === "move" ? "move" : confirm.apiAction]}
              </div>
              {confirm.preview.length > 0 && (
                <div style={{ fontSize: 12, background: c.panel, borderRadius: 10, padding: 10, marginBottom: 10, lineHeight: 1.7 }}>
                  {confirm.preview.map((p, i) => <div key={i}>{p}</div>)}
                  {confirm.n > confirm.preview.length && <div style={{ color: c.dim }}>… +{confirm.n - confirm.preview.length}</div>}
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
                <button type="button" className="sc-ghost" disabled={busy} onClick={() => setConfirm(null)} style={footerBtn("ghost", busy)}>{t.cancel}</button>
                <button type="button" disabled={busy} onClick={runConfirmed} style={footerBtn("primary", busy, busy ? {} : { background: actColor[confirm.action], boxShadow: "none" })}>
                  {busy ? t.working : t.confirm}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Separation dialog (Resign / Terminate via Bulk Actions) */}
        {sep && (
          <div style={overlay} onClick={() => !sep.busy && sep.step !== "running" && setSep(null)}>
            <div className="sc-noscroll" style={card} onClick={(e) => e.stopPropagation()}>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>{t.sep.title(sep.rows.length)}</div>
              <div style={{ ...st.hint, marginBottom: 12 }}>{t.sep.hint}</div>

              {sep.step === "form" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <div style={{ display: "flex", gap: 8 }}>
                    {["resignation", "terminate"].map((k) => (
                      <label key={k} style={{ flex: 1, display: "flex", gap: 8, alignItems: "center", padding: "10px 12px", borderRadius: 10, cursor: "pointer", fontSize: 14, fontWeight: 600, border: `1.5px solid ${sep.actionType === k ? "#dc2626" : c.border}` }}>
                        <input type="radio" checked={sep.actionType === k} onChange={() => setSep((x) => ({ ...x, actionType: k }))} />
                        {t.sep[k]}
                      </label>
                    ))}
                  </div>
                  <label style={st.label}>{t.sep.lwd}</label>
                  <input type="date" className="sc-input" value={sep.lastWorkingDay} onChange={(e) => setSep((x) => ({ ...x, lastWorkingDay: e.target.value }))} style={inputStyle} />
                  {sep.actionType === "resignation" ? (
                    <>
                      <label style={st.label}>{t.sep.reason}</label>
                      <select className="sc-input" value={sep.resignationReason} onChange={(e) => setSep((x) => ({ ...x, resignationReason: e.target.value }))} style={inputStyle}>
                        <option value="">—</option>
                        {Object.entries(bt.resignationReasons || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </>
                  ) : (
                    <>
                      <label style={st.label}>{t.sep.termType}</label>
                      <select className="sc-input" value={sep.terminationType} onChange={(e) => setSep((x) => ({ ...x, terminationType: e.target.value }))} style={inputStyle}>
                        <option value="">—</option>
                        {Object.entries(bt.terminationTypes || {}).filter(([k]) => k !== "voluntary").map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </>
                  )}
                  <label style={st.label}>{sep.actionType === "resignation" ? t.sep.details : t.sep.reason}</label>
                  <textarea rows={2} className="sc-input" value={sep.reason} onChange={(e) => setSep((x) => ({ ...x, reason: e.target.value }))} style={{ ...inputStyle, resize: "vertical" }} />
                </div>
              )}

              {(sep.step === "review" || sep.step === "running" || sep.step === "done") && sep.validation && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", fontSize: 13 }}>
                    <span style={miniBtn("#16a34a")}>{t.sep.valid}: {sep.validation.totals.valid}</span>
                    <span style={miniBtn("#d97706")}>{t.sep.warning}: {sep.validation.totals.warning}</span>
                    <span style={miniBtn("#dc2626")}>{t.sep.error}: {sep.validation.totals.error}</span>
                  </div>
                  <div style={{ maxHeight: 220, overflow: "auto", fontSize: 12, background: c.panel, borderRadius: 10, padding: 10, lineHeight: 1.7 }}>
                    {sep.validation.rows.filter((r) => r.messages.length).map((r) => (
                      <div key={r.rowId} style={{ color: r.status === "error" ? "#dc2626" : "#d97706" }}>
                        <b>{r.clientCode || r.employee?.clientCode || "—"}</b>: {r.messages.map((m) => translateBulkMessage(bt, m)).join(" · ")}
                      </div>
                    ))}
                    {!sep.validation.rows.some((r) => r.messages.length) && <div style={{ color: "#16a34a" }}>✓</div>}
                  </div>
                </div>
              )}

              {(sep.step === "running" || sep.step === "done") && sep.batch && (
                <div style={{ marginTop: 10, fontSize: 13 }}>
                  <b>{sep.step === "running" ? t.sep.running : t.sep.finished}</b> — {sep.batch.processedCount}/{sep.batch.queuedRows}
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
                    {Object.entries(sep.batch.byOutcome || {}).map(([k, n]) => (
                      <span key={k} style={miniBtn(k === "failed" || k === "skipped" ? "#dc2626" : "#16a34a")}>{bt.outcomes?.[k] || k}: {n}</span>
                    ))}
                  </div>
                </div>
              )}
              {sep.step === "running" && !sep.batch && <div style={{ marginTop: 10, fontSize: 13 }}><Loader2 size={14} className="nx-spin" /> {t.sep.running}</div>}

              {sep.err && <div style={{ marginTop: 10 }}>{notice("error", sep.err)}</div>}

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
                {sep.step === "form" && <>
                  <button type="button" className="sc-ghost" onClick={() => setSep(null)} disabled={sep.busy} style={footerBtn("ghost", sep.busy)}>{t.cancel}</button>
                  <button type="button" className="sc-primary" onClick={sepCheck} disabled={sep.busy || !sep.lastWorkingDay} style={footerBtn("primary", sep.busy || !sep.lastWorkingDay)}>{sep.busy ? t.working : t.sep.check}</button>
                </>}
                {sep.step === "review" && <>
                  <button type="button" className="sc-ghost" onClick={() => setSep((x) => ({ ...x, step: "form", validation: null, err: "" }))} disabled={sep.busy} style={footerBtn("ghost", sep.busy)}>{t.sep.back}</button>
                  <button type="button" onClick={sepSubmit}
                    disabled={sepBlocked}
                    style={footerBtn("primary", sepBlocked, sepBlocked ? {} : { background: "#dc2626", boxShadow: "none" })}>
                    {t.sep.submit(sep.validation.totals.valid + sep.validation.totals.warning)}
                  </button>
                </>}
                {sep.step === "done" && <button type="button" className="sc-primary" onClick={sepDone} style={footerBtn("primary", false)}>{t.sep.done}</button>}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
